import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  ArrowLeft, 
  Plus,
  Calendar as CalendarIcon,
  Loader2,
  TrendingUp,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Info,
  Globe,
  Landmark,
  ScrollText
} from 'lucide-react';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const riskColors = {
  'Low': 'bg-green-100 text-green-700',
  'Low-Moderate': 'bg-blue-100 text-blue-700',
  'Moderate': 'bg-yellow-100 text-yellow-700',
  'Moderate-High': 'bg-orange-100 text-orange-700',
  'High': 'bg-red-100 text-red-700'
};

export default function AddPortfolio() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const [assets, setAssets] = useState({ stocks: [], uttFunds: [], etfs: [], bonds: [] });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [date, setDate] = useState(new Date());
  
  // Determine initial tab from URL params
  const getInitialTab = () => {
    const type = searchParams.get('type');
    if (type === 'utt') return 'utt';
    if (type === 'etf') return 'etfs';
    if (type === 'bond') return 'bonds';
    return 'stocks';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [formData, setFormData] = useState({
    symbol: searchParams.get('symbol') || '',
    quantity: '',
    purchase_price: '',
    notes: '',
    asset_type: searchParams.get('type') || 'stock'
  });

  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/investable-assets`);
      const stocks = response.data.assets.filter(a => a.type === 'stock');
      const uttFunds = response.data.assets.filter(a => a.type === 'utt');
      const etfs = response.data.assets.filter(a => a.type === 'etf');
      const bonds = response.data.assets.filter(a => a.type === 'bond');
      setAssets({ stocks, uttFunds, etfs, bonds });
    } catch (error) {
      toast.error(t('Failed to load assets', 'Imeshindwa kupakia mali'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  useEffect(() => {
    if (formData.symbol) {
      const allAssets = [...assets.stocks, ...assets.uttFunds, ...assets.etfs, ...assets.bonds];
      const asset = allAssets.find(a => a.symbol === formData.symbol);
      setSelectedAsset(asset);
      if (asset) {
        setFormData(prev => {
          if (!prev.purchase_price) {
            return { ...prev, purchase_price: asset.current_price.toString() };
          }
          return prev;
        });
      }
    }
  }, [formData.symbol, assets]);

  const handleAssetSelect = (symbol, assetType) => {
    setFormData({
      ...formData,
      symbol,
      asset_type: assetType,
      purchase_price: ''
    });
  };
  
  const getAssetTypeFromTab = (tab) => {
    switch(tab) {
      case 'stocks': return 'stock';
      case 'utt': return 'utt';
      case 'etfs': return 'etf';
      case 'bonds': return 'bond';
      default: return 'stock';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.symbol || !formData.quantity || !formData.purchase_price) {
      toast.error(t('Please fill all required fields', 'Tafadhali jaza sehemu zote zinazohitajika'));
      return;
    }

    if (parseFloat(formData.quantity) <= 0 || parseFloat(formData.purchase_price) <= 0) {
      toast.error(t('Quantity and price must be positive', 'Idadi na bei lazima ziwe chanya'));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/portfolio`, {
        symbol: formData.symbol,
        quantity: parseFloat(formData.quantity),
        purchase_price: parseFloat(formData.purchase_price),
        purchase_date: format(date, 'yyyy-MM-dd'),
        notes: formData.notes || null,
        asset_type: formData.asset_type
      });
      
      // Check if this is user's first investment
      const portfolioCheck = await axios.get(`${API}/portfolio`);
      const isFirstInvestment = portfolioCheck.data.items?.length === 1;
      
      if (isFirstInvestment) {
        toast.success(t('First investment added! See your portfolio score now!', 'Uwekezaji wa kwanza umeongezwa! Ona alama ya portfolio yako sasa!'));
        navigate('/intelligence'); // Show portfolio intelligence for first-time users
      } else {
        toast.success(t('Investment added successfully!', 'Uwekezaji umeongezwa!'));
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed to add investment', 'Imeshindwa kuongeza uwekezaji'));
    } finally {
      setSubmitting(false);
    }
  };

  const totalValue = formData.quantity && formData.purchase_price 
    ? (parseFloat(formData.quantity) * parseFloat(formData.purchase_price))
    : 0;

  const getCurrentAssets = () => {
    switch(activeTab) {
      case 'stocks': return assets.stocks;
      case 'utt': return assets.uttFunds;
      case 'etfs': return assets.etfs;
      case 'bonds': return assets.bonds;
      default: return assets.stocks;
    }
  };
  
  const currentAssets = getCurrentAssets();
  
  // Get quantity label based on asset type
  const getQuantityLabel = () => {
    switch(activeTab) {
      case 'stocks': return t('Number of Shares', 'Idadi ya Hisa');
      case 'utt': return t('Number of Units', 'Idadi ya Vipande');
      case 'etfs': return t('Number of Units', 'Idadi ya Vipande');
      case 'bonds': return t('Face Value Units (TZS 1M each)', 'Vitengo vya Thamani (TZS 1M kila moja)');
      default: return t('Quantity', 'Idadi');
    }
  };
  
  // Get price label based on asset type
  const getPriceLabel = () => {
    switch(activeTab) {
      case 'stocks': return t('Purchase Price per Share (TZS)', 'Bei ya Hisa (TZS)');
      case 'utt': return t('Purchase Price per Unit (TZS)', 'Bei ya Kipande (TZS)');
      case 'etfs': return t('Purchase Price per Unit (TZS)', 'Bei ya Kipande (TZS)');
      case 'bonds': return t('Face Value (TZS)', 'Thamani ya Uso (TZS)');
      default: return t('Purchase Price (TZS)', 'Bei ya Ununuzi (TZS)');
    }
  };

  return (
    <Layout>
      <div className="page-enter max-w-2xl mx-auto">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4 -ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t('Back', 'Rudi')}
        </Button>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              {t('Add Investment', 'Ongeza Uwekezaji')}
            </CardTitle>
            <CardDescription>
              {t('Record stocks, funds, ETFs, or bonds in your portfolio', 'Rekodi hisa, mifuko, ETF, au hatifungani katika portfolio yako')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Asset Type Tabs */}
              <Tabs value={activeTab} onValueChange={(v) => {
                setActiveTab(v);
                setFormData({
                  ...formData,
                  symbol: '',
                  purchase_price: '',
                  asset_type: getAssetTypeFromTab(v)
                });
                setSelectedAsset(null);
              }}>
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="stocks" data-testid="tab-stocks" className="flex items-center gap-1 text-xs sm:text-sm">
                    <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">{t('Stocks', 'Hisa')}</span>
                    <span className="sm:hidden">DSE</span>
                  </TabsTrigger>
                  <TabsTrigger value="utt" data-testid="tab-utt" className="flex items-center gap-1 text-xs sm:text-sm">
                    <PieChart className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">{t('UTT Funds', 'UTT')}</span>
                    <span className="sm:hidden">UTT</span>
                  </TabsTrigger>
                  <TabsTrigger value="etfs" data-testid="tab-etfs" className="flex items-center gap-1 text-xs sm:text-sm">
                    <Globe className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>{t('ETFs', 'ETF')}</span>
                  </TabsTrigger>
                  <TabsTrigger value="bonds" data-testid="tab-bonds" className="flex items-center gap-1 text-xs sm:text-sm">
                    <Landmark className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">{t('Bonds', 'Bondi')}</span>
                    <span className="sm:hidden">T-Bill</span>
                  </TabsTrigger>
                </TabsList>

                {/* Stocks Tab */}
                <TabsContent value="stocks" className="mt-4">
                  <div className="space-y-2">
                    <Label>{t('Select Stock', 'Chagua Hisa')} *</Label>
                    <Select 
                      value={formData.symbol} 
                      onValueChange={(value) => handleAssetSelect(value, 'stock')}
                    >
                      <SelectTrigger className="h-12" data-testid="stock-select">
                        <SelectValue placeholder={t('Choose a stock', 'Chagua hisa')} />
                      </SelectTrigger>
                      <SelectContent>
                        {assets.stocks.map(stock => (
                          <SelectItem key={stock.symbol} value={stock.symbol}>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{stock.symbol}</span>
                              <span className="text-muted-foreground">- {stock.name}</span>
                              <span className={`text-xs ${stock.ytd_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {stock.ytd_change >= 0 ? '+' : ''}{stock.ytd_change?.toFixed(1)}%
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>

                {/* UTT Funds Tab */}
                <TabsContent value="utt" className="mt-4">
                  <div className="space-y-2">
                    <Label>{t('Select UTT AMIS Fund', 'Chagua Mfuko wa UTT AMIS')} *</Label>
                    <Select 
                      value={formData.symbol} 
                      onValueChange={(value) => handleAssetSelect(value, 'utt')}
                    >
                      <SelectTrigger className="h-12" data-testid="utt-select">
                        <SelectValue placeholder={t('Choose a fund', 'Chagua mfuko')} />
                      </SelectTrigger>
                      <SelectContent>
                        {assets.uttFunds.map(fund => (
                          <SelectItem key={fund.symbol} value={fund.symbol}>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{language === 'sw' ? fund.name_sw : fund.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {fund.annual_return}% {t('return', 'faida')}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* UTT Info */}
                    <div className="flex items-start gap-2 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg mt-2">
                      <Info className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-teal-700 dark:text-teal-300">
                        {t(
                          'Enter the number of units you own. Unit price is the current sale price from UTT AMIS.',
                          'Ingiza idadi ya vitengo unavyo. Bei ya kitengo ni bei ya sasa ya mauzo kutoka UTT AMIS.'
                        )}
                      </p>
                    </div>
                  </div>
                </TabsContent>

                {/* ETFs Tab */}
                <TabsContent value="etfs" className="mt-4">
                  <div className="space-y-2">
                    <Label>{t('Select ETF', 'Chagua ETF')} *</Label>
                    <Select 
                      value={formData.symbol} 
                      onValueChange={(value) => handleAssetSelect(value, 'etf')}
                    >
                      <SelectTrigger className="h-12" data-testid="etf-select">
                        <SelectValue placeholder={t('Choose an ETF', 'Chagua ETF')} />
                      </SelectTrigger>
                      <SelectContent>
                        {assets.etfs.map(etf => (
                          <SelectItem key={etf.symbol} value={etf.symbol}>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{etf.symbol}</span>
                              <span className="text-muted-foreground text-xs">- {etf.name}</span>
                              <Badge variant="outline" className={`text-xs ${etf.ytd_change >= 0 ? 'border-green-500 text-green-600' : 'border-red-500 text-red-600'}`}>
                                {etf.ytd_change >= 0 ? '+' : ''}{etf.ytd_change}%
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* ETF Info */}
                    <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg mt-2">
                      <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        {t(
                          'ETFs trade on DSE like stocks. Enter the number of units and the price you paid per unit.',
                          'ETF zinafanyiwa biashara DSE kama hisa. Ingiza idadi ya vitengo na bei uliyolipa kwa kila kitengo.'
                        )}
                      </p>
                    </div>
                  </div>
                </TabsContent>

                {/* Bonds/T-Bills Tab */}
                <TabsContent value="bonds" className="mt-4">
                  <div className="space-y-2">
                    <Label>{t('Select Bond or T-Bill', 'Chagua Hatifungani au Dhamana')} *</Label>
                    <Select 
                      value={formData.symbol} 
                      onValueChange={(value) => handleAssetSelect(value, 'bond')}
                    >
                      <SelectTrigger className="h-12" data-testid="bond-select">
                        <SelectValue placeholder={t('Choose a bond or T-Bill', 'Chagua hatifungani au dhamana')} />
                      </SelectTrigger>
                      <SelectContent>
                        {assets.bonds.map(bond => (
                          <SelectItem key={bond.symbol} value={bond.symbol}>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{bond.symbol}</span>
                              <span className="text-muted-foreground text-xs">- {bond.name}</span>
                              <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
                                {bond.annual_return}% {t('yield', 'faida')}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Bond Info */}
                    <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg mt-2">
                      <ScrollText className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        {t(
                          'Government bonds and T-Bills are issued by Bank of Tanzania. Minimum investment is TZS 500,000 for T-Bills and TZS 1,000,000 for bonds.',
                          'Hatifungani na Dhamana za serikali zinatolewa na Benki Kuu ya Tanzania. Uwekezaji wa chini ni TZS 500,000 kwa T-Bills na TZS 1,000,000 kwa hatifungani.'
                        )}
                      </p>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Selected Asset Info */}
              {selectedAsset && (
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-foreground">
                          {language === 'sw' ? (selectedAsset.name_sw || selectedAsset.name) : selectedAsset.name}
                        </p>
                        <Badge variant="outline" className="text-xs">
                          {selectedAsset.type === 'utt' ? 'UTT AMIS' : 
                           selectedAsset.type === 'etf' ? 'ETF' :
                           selectedAsset.type === 'bond' ? (selectedAsset.symbol?.startsWith('TBILL') ? 'T-Bill' : 'Bond') :
                           'DSE'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{selectedAsset.category}</p>
                      {selectedAsset.risk_level && (
                        <Badge className={`mt-1 text-xs ${riskColors[selectedAsset.risk_level] || 'bg-gray-100'}`}>
                          {selectedAsset.risk_level} {t('Risk', 'Hatari')}
                        </Badge>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-foreground">
                        TZS {selectedAsset.current_price?.toLocaleString()}
                      </p>
                      <p className={`text-sm flex items-center justify-end gap-1 ${
                        selectedAsset.ytd_change >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {selectedAsset.ytd_change >= 0 ? (
                          <ArrowUpRight className="w-3 h-3" />
                        ) : (
                          <ArrowDownRight className="w-3 h-3" />
                        )}
                        {selectedAsset.ytd_change >= 0 ? '+' : ''}{selectedAsset.ytd_change?.toFixed(2)}% 
                        {selectedAsset.type === 'bond' ? t('Yield', 'Faida') : 'YTD'}
                      </p>
                      {selectedAsset.min_investment && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Min: TZS {selectedAsset.min_investment?.toLocaleString()}
                        </p>
                      )}
                      {selectedAsset.maturity && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {t('Maturity', 'Ukomavu')}: {selectedAsset.maturity}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                {/* Quantity */}
                <div className="space-y-2">
                  <Label htmlFor="quantity">
                    {getQuantityLabel()} *
                  </Label>
                  <Input
                    id="quantity"
                    type="number"
                    placeholder={activeTab === 'stocks' ? 'e.g. 100' : activeTab === 'bonds' ? 'e.g. 1' : 'e.g. 50'}
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                    className="h-12"
                    min="0"
                    step="any"
                    data-testid="quantity-input"
                  />
                </div>

                {/* Purchase Price */}
                <div className="space-y-2">
                  <Label htmlFor="price">
                    {getPriceLabel()} *
                  </Label>
                  <Input
                    id="price"
                    type="number"
                    placeholder="e.g. 1500"
                    value={formData.purchase_price}
                    onChange={(e) => setFormData({...formData, purchase_price: e.target.value})}
                    className="h-12"
                    min="0"
                    step="any"
                    data-testid="price-input"
                  />
                </div>
              </div>

              {/* Purchase Date */}
              <div className="space-y-2">
                <Label>{t('Purchase Date', 'Tarehe ya Ununuzi')} *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-12 justify-start text-left font-normal"
                      data-testid="date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => d && setDate(d)}
                      disabled={(date) => date > new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">{t('Notes (Optional)', 'Maelezo (Si lazima)')}</Label>
                <Textarea
                  id="notes"
                  placeholder={t('Any additional notes about this investment...', 'Maelezo yoyote ya ziada kuhusu uwekezaji huu...')}
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  data-testid="notes-input"
                />
              </div>

              {/* Total Value Preview */}
              {totalValue > 0 && (
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      {t('Total Investment Value', 'Thamani Jumla ya Uwekezaji')}
                    </span>
                    <span className="text-xl font-bold text-primary" data-testid="total-value">
                      TZS {totalValue.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full h-12 rounded-full text-base btn-press"
                disabled={submitting}
                data-testid="submit-investment-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('Adding...', 'Inaongeza...')}
                  </>
                ) : (
                  <>
                    <TrendingUp className="w-4 h-4 mr-2" />
                    {t('Add Investment', 'Ongeza Uwekezaji')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
